import {
  getCollectionStartTimeInitialValue,
  serializeCollectionStartTime,
} from './collection-time';

const requireMetadataItem = (items, key, value, message) => {
  const item = items.find((candidate) => candidate[key] === value);
  if (!item) {
    throw new Error(message);
  }
  return item;
};

const MODIFIED_TIME_FIELD_TYPE = 1002;
const EVENT_DOMAINS = new Set(['expense', 'income', 'plan', 'salary']);
const EVENT_VALUE_TYPES = new Set(['amount', 'quantity', 'ratio']);

const createLatestRequestGuard = () => {
  let latestRequestId = 0;
  return {
    start: () => {
      latestRequestId += 1;
      return latestRequestId;
    },
    isLatest: (requestId) => requestId === latestRequestId,
    invalidate: () => {
      latestRequestId += 1;
    },
  };
};

const getFeishuSourceSelection = (sourceReference) => {
  if (typeof sourceReference !== 'string' || !sourceReference.startsWith('http')) {
    return {};
  }
  const url = new URL(sourceReference);
  return {
    ...(url.searchParams.get('table') ? { tableId: url.searchParams.get('table') } : {}),
    ...(url.searchParams.get('view') ? { viewId: url.searchParams.get('view') } : {}),
  };
};

const getFeishuCollectionInitialValues = (record, fixedEventDomain) => {
  if (!record) {
    return {
      ...(fixedEventDomain !== 'expense'
        ? {
            sourceConfig: {
              collectionMode: 'incremental',
            },
          }
        : {}),
      fieldMapping: {
        eventDomain: fixedEventDomain || 'income',
        eventFieldIds: [],
        eventValueTypes: {},
      },
    };
  }
  const events = record.fieldMapping.events || [];
  return {
    ...record,
    sourceConfig: {
      ...record.sourceConfig,
      ...((fixedEventDomain || record.fieldMapping.eventDomain) !== 'expense'
        ? { collectionMode: record.sourceConfig.collectionMode ?? 'incremental' }
        : {}),
      startTime: getCollectionStartTimeInitialValue(record.sourceConfig.startTime),
    },
    sourceReference: record.sourceConfig.sourceUrl || '',
    fieldMapping: {
      ...record.fieldMapping,
      eventDomain: fixedEventDomain || record.fieldMapping.eventDomain,
      eventFieldIds: events.map(({ fieldId }) => fieldId),
      eventValueTypes: Object.fromEntries(
        events
          .filter(({ valueType }) => valueType)
          .map(({ fieldId, valueType }) => [fieldId, valueType]),
      ),
    },
  };
};

const getFeishuCursorFieldOptions = (fields) =>
  fields
    .filter(({ type }) => type === MODIFIED_TIME_FIELD_TYPE)
    .map(({ fieldId, name }) => ({ label: name, value: fieldId }));

const getFeishuCollectionResetValues = (sourceConfig) => ({
  sourceConfig: { ...sourceConfig, cursorFieldId: undefined },
  fieldMapping: {
    eventDateFieldId: undefined,
    operationMonthFieldId: undefined,
    grossMarginPeriodFieldId: undefined,
    rentStartedAtFieldId: undefined,
    rentEndedAtFieldId: undefined,
    operationEntityCodeFieldId: undefined,
    sourceStoreNameFieldId: undefined,
    eventFieldIds: [],
    eventValueTypes: {},
  },
});

const getFeishuEventDomainResetValues = (eventDomain) => ({
  fieldMapping: {
    eventDomain,
    eventDateFieldId: undefined,
    operationMonthFieldId: undefined,
    grossMarginPeriodFieldId: undefined,
    rentStartedAtFieldId: undefined,
    rentEndedAtFieldId: undefined,
    operationEntityCodeFieldId: undefined,
    sourceStoreNameFieldId: undefined,
    eventFieldIds: [],
    eventValueTypes: {},
  },
});

const buildFeishuCollectionValues = (values, metadata, fixedEventDomain, dateMappingMode) => {
  const { sourceConfig } = values;
  const fieldMapping = {
    ...values.fieldMapping,
    ...(fixedEventDomain ? { eventDomain: fixedEventDomain } : {}),
  };
  if (!EVENT_DOMAINS.has(fieldMapping.eventDomain)) {
    throw new Error('经营事件子领域不正确');
  }
  const collectionMode = sourceConfig.collectionMode ?? 'incremental';
  if (fieldMapping.eventDomain !== 'expense' && !['incremental', 'full'].includes(collectionMode)) {
    throw new Error('采集模式不正确');
  }
  const table = requireMetadataItem(
    metadata.tables,
    'tableId',
    sourceConfig.tableId,
    '数据表已不存在，请重新选择',
  );
  const view = requireMetadataItem(
    metadata.views,
    'viewId',
    sourceConfig.viewId,
    '视图已不存在，请重新选择',
  );
  const cursorField = requireMetadataItem(
    metadata.fields,
    'fieldId',
    sourceConfig.cursorFieldId,
    '最后更新时间字段已不存在，请重新选择',
  );
  if (cursorField.type !== MODIFIED_TIME_FIELD_TYPE) {
    throw new Error('最后更新时间字段必须是 ModifiedTime 类型，请重新选择');
  }
  const grossMarginPeriodMode = dateMappingMode === 'gross-margin-period';
  const eventDateFieldId = grossMarginPeriodMode
    ? undefined
    : requireMetadataItem(
        metadata.fields,
        'fieldId',
        fieldMapping.eventDateFieldId,
        '经营日期字段已不存在，请重新选择',
      ).fieldId;
  const operationMonthFieldId = grossMarginPeriodMode
    ? requireMetadataItem(
        metadata.fields,
        'fieldId',
        fieldMapping.operationMonthFieldId,
        '经营月份字段已不存在，请重新选择',
      ).fieldId
    : undefined;
  const grossMarginPeriodFieldId = grossMarginPeriodMode
    ? requireMetadataItem(
        metadata.fields,
        'fieldId',
        fieldMapping.grossMarginPeriodFieldId,
        '毛利率时段字段已不存在，请重新选择',
      ).fieldId
    : undefined;
  requireMetadataItem(
    metadata.fields,
    'fieldId',
    fieldMapping.operationEntityCodeFieldId,
    '经营实体编码字段已不存在，请重新选择',
  );
  const sourceStoreNameFieldId =
    fieldMapping.eventDomain === 'plan'
      ? undefined
      : requireMetadataItem(
          metadata.fields,
          'fieldId',
          fieldMapping.sourceStoreNameFieldId,
          '来源门店名称字段已不存在，请重新选择',
        ).fieldId;
  const expenseDomain = fieldMapping.eventDomain === 'expense';
  const hasRentStartedAtField = expenseDomain && Boolean(fieldMapping.rentStartedAtFieldId);
  const hasRentEndedAtField = expenseDomain && Boolean(fieldMapping.rentEndedAtFieldId);
  if (hasRentStartedAtField !== hasRentEndedAtField) {
    throw new Error('费用适用起止日期字段必须同时配置');
  }
  const rentStartedAtFieldId = hasRentStartedAtField
    ? requireMetadataItem(
        metadata.fields,
        'fieldId',
        fieldMapping.rentStartedAtFieldId,
        '费用适用开始日期字段已不存在，请重新选择',
      ).fieldId
    : undefined;
  const rentEndedAtFieldId = hasRentEndedAtField
    ? requireMetadataItem(
        metadata.fields,
        'fieldId',
        fieldMapping.rentEndedAtFieldId,
        '费用适用结束日期字段已不存在，请重新选择',
      ).fieldId
    : undefined;
  const eventFieldIds = fieldMapping.eventFieldIds || [];
  if (!eventFieldIds.length) {
    throw new Error('请至少选择一个经营事件字段');
  }
  const roleFieldIds = [
    ...(eventDateFieldId ? [eventDateFieldId] : []),
    ...(operationMonthFieldId ? [operationMonthFieldId] : []),
    ...(grossMarginPeriodFieldId ? [grossMarginPeriodFieldId] : []),
    ...(rentStartedAtFieldId ? [rentStartedAtFieldId, rentEndedAtFieldId] : []),
    fieldMapping.operationEntityCodeFieldId,
    ...(sourceStoreNameFieldId ? [sourceStoreNameFieldId] : []),
    ...eventFieldIds,
  ];
  if (new Set(roleFieldIds).size !== roleFieldIds.length) {
    throw new Error(
      grossMarginPeriodMode
        ? '同一字段不能同时用于门店名称、经营实体编码、经营月份、毛利率时段和经营事件'
        : '同一字段不能同时用于门店名称、经营实体编码、经营日期和经营事件',
    );
  }
  const events = eventFieldIds.map((fieldId) => {
    const field = requireMetadataItem(
      metadata.fields,
      'fieldId',
      fieldId,
      '经营事件字段已不存在，请重新选择',
    );
    const valueType =
      fieldMapping.eventDomain === 'expense' ? 'amount' : fieldMapping.eventValueTypes?.[fieldId];
    if (!valueType) {
      throw new Error(`请选择“${field.name}”的数值类型`);
    }
    if (!EVENT_VALUE_TYPES.has(valueType)) {
      throw new Error(`“${field.name}”的数值类型不正确`);
    }
    if (fieldMapping.eventDomain !== 'plan') {
      return {
        fieldId,
        typeLevel1: table.name,
        typeLevel2: view.name,
        typeLevel3: field.name,
        valueType,
      };
    }
    return { fieldId, planItemName: field.name, valueType };
  });
  return {
    connectionId: values.connectionId,
    name: values.name,
    sourceConfig: {
      sourceUrl: values.sourceReference.trim(),
      appToken: sourceConfig.appToken,
      cursorFieldId: sourceConfig.cursorFieldId,
      tableId: sourceConfig.tableId,
      viewId: sourceConfig.viewId,
      startTime: serializeCollectionStartTime(sourceConfig.startTime),
      ...(fieldMapping.eventDomain !== 'expense' ? { collectionMode } : {}),
    },
    fieldMapping: {
      eventDomain: fieldMapping.eventDomain,
      ...(eventDateFieldId ? { eventDateFieldId } : {}),
      ...(operationMonthFieldId ? { operationMonthFieldId } : {}),
      ...(grossMarginPeriodFieldId ? { grossMarginPeriodFieldId } : {}),
      ...(rentStartedAtFieldId ? { rentStartedAtFieldId, rentEndedAtFieldId } : {}),
      operationEntityCodeFieldId: fieldMapping.operationEntityCodeFieldId,
      ...(sourceStoreNameFieldId ? { sourceStoreNameFieldId } : {}),
      events,
    },
  };
};

export {
  buildFeishuCollectionValues,
  createLatestRequestGuard,
  getFeishuCollectionInitialValues,
  getFeishuEventDomainResetValues,
  getFeishuCollectionResetValues,
  getFeishuCursorFieldOptions,
  getFeishuSourceSelection,
};
